import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Sparkles, Heart, Flower2, PenTool } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { CustomOrderModal } from './CustomOrderModal';
const HERO_IMAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/v4nWU5iigTRC7F7xQdby14/Edit44.png',
  alt: 'FerbyStore signature pink gift box being opened, revealing a handcrafted decorated mirror'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nQEYMtXtKVsktMC2AFtvgD/Edit45.png',
  alt: 'FerbyStore signature pink packaging boxes with a handcrafted purse'
}];
export function Hero() {
  const {
    t
  } = useTranslation();
  const [index, setIndex] = useState(0);
  const [customOpen, setCustomOpen] = useState(false);
  // Auto-rotate the main hero image every 4.5 seconds.
  useEffect(() => {
    const id = setInterval(() => {
      setIndex(i => (i + 1) % HERO_IMAGES.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);
  // Overlay always shows the OTHER image so they trade places.
  const mainImage = HERO_IMAGES[index];
  const overlayImage = HERO_IMAGES[(index + 1) % HERO_IMAGES.length];
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-cream-50" data-id="element-380">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-381">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-lavender-100/40 blur-3xl" data-id="element-382" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-blush-100/40 blur-3xl" data-id="element-383" />
      </div>

      <motion.div animate={{
      y: [0, -15, 0],
      rotate: [0, 5, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-32 left-[10%] hidden lg:block text-lavender-300" data-id="element-384">
        <Sparkles size={32} data-id="element-385" />
      </motion.div>
      <motion.div animate={{
      y: [0, 20, 0],
      rotate: [0, -10, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-40 right-[15%] hidden lg:block text-blush-300" data-id="element-386">
        <Flower2 size={40} data-id="element-387" />
      </motion.div>
      <motion.div animate={{
      scale: [1, 1.1, 1]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} className="absolute top-1/2 left-[5%] hidden lg:block text-gold-500/40" data-id="element-388">
        <Heart size={24} data-id="element-389" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-390">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-391">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center lg:text-left" data-id="element-392">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-lavender-100 shadow-sm mb-8" data-id="element-393">
              <Sparkles className="w-4 h-4 text-lavender-500" data-id="element-394" />
              <span className="text-sm font-medium text-gray-600 uppercase tracking-wider" data-id="element-395">
                {t.hero.badge}
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-6xl xl:text-7xl text-gray-900 leading-tight mb-6" data-id="element-396">
              {t.hero.titleLine1}{' '}
              <span className="text-lavender-600" data-id="element-397">{t.hero.titleLine2}</span>
            </h1>

            <p className="font-serif italic text-2xl lg:text-3xl text-gray-600 mb-8 leading-relaxed" data-id="element-398">
              {t.hero.subtitle}
            </p>

            <p className="text-lg text-gray-600 mb-10 max-w-xl mx-auto lg:mx-0" data-id="element-399">
              {t.hero.intro}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-400">
              <a href="https://shop.ferbystore.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-lavender-500 hover:bg-lavender-600 shadow-md hover:shadow-lg transition-all hover:-translate-y-1" data-id="element-401">
                <ShoppingBag className="w-5 h-5 mr-2" data-id="element-402" />
                {t.hero.shopCta}
              </a>
              <button type="button" onClick={() => setCustomOpen(true)} className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-lavender-600 bg-white border-2 border-lavender-200 hover:border-lavender-500 hover:bg-lavender-50 transition-all" data-id="element-403">
                <PenTool className="w-5 h-5 mr-2" data-id="element-404" />
                {t.hero.customCta}
              </button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-405">
            {/* Main image — auto-swaps between the two hero photos */}
            <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white aspect-square bg-cream-100" data-id="element-406">
              <div className="absolute inset-0 bg-gradient-to-tr from-lavender-500/15 to-transparent mix-blend-overlay z-20 pointer-events-none" data-id="element-407"></div>

              <AnimatePresence mode="sync" data-id="element-408">
                <motion.img key={mainImage.src} src={mainImage.src} alt={mainImage.alt} initial={{
                opacity: 0,
                scale: 1.06
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 1.02
              }} transition={{
                opacity: {
                  duration: 1.1,
                  ease: 'easeInOut'
                },
                scale: {
                  duration: 6,
                  ease: 'easeOut'
                }
              }} className="absolute inset-0 w-full h-full object-cover" data-id="element-409" />
              </AnimatePresence>

              {/* Sparkle accent */}
              <motion.div animate={{
              y: [0, -10, 0],
              rotate: [0, 12, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-5 left-5 z-30" data-id="element-410">
                <div className="w-11 h-11 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-411">
                  <Sparkles className="w-5 h-5 text-gold-500" data-id="element-412" />
                </div>
              </motion.div>

              {/* Slide dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2" data-id="element-413">
                {HERO_IMAGES.map((_, i) => <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`Show image ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'w-7 bg-white shadow' : 'w-2 bg-white/60 hover:bg-white/90'}`} data-id="element-414" />)}
              </div>
            </motion.div>

            {/* Overlay card — always shows the OTHER image, so the pair trade places */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            rotate: -6
          }} animate={{
            opacity: 1,
            y: 0,
            rotate: -6
          }} transition={{
            duration: 0.9,
            delay: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            rotate: -2,
            y: -6,
            scale: 1.03
          }} className="absolute -bottom-10 -left-6 sm:-left-10 w-36 sm:w-44 lg:w-48 z-20" data-id="element-415">
              <motion.div animate={{
              y: [0, 6, 0]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} className="rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-white aspect-square relative" data-id="element-416">
                <AnimatePresence mode="sync" data-id="element-417">
                  <motion.img key={overlayImage.src} src={overlayImage.src} alt={overlayImage.alt} initial={{
                  opacity: 0,
                  scale: 1.08
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 1.02
                }} transition={{
                  opacity: {
                    duration: 1.1,
                    ease: 'easeInOut'
                  },
                  scale: {
                    duration: 6,
                    ease: 'easeOut'
                  }
                }} className="absolute inset-0 w-full h-full object-cover" data-id="element-418" />
                </AnimatePresence>
              </motion.div>

              {/* Floating heart badge on the corner */}
              <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-blush-400 flex items-center justify-center shadow-md ring-4 ring-white" data-id="element-419">
                <Heart className="w-4 h-4 text-white fill-white" data-id="element-420" />
              </motion.div>
            </motion.div>

            {/* Decorative blobs */}
            <div className="absolute -top-6 -right-6 w-28 h-28 bg-gold-100 rounded-full -z-10 blur-xl" data-id="element-421"></div>
            <div className="absolute -bottom-12 left-10 w-36 h-36 bg-blush-100 rounded-full -z-10 blur-xl" data-id="element-422"></div>
            <div className="absolute top-1/3 -right-10 w-32 h-32 bg-lavender-100 rounded-full -z-10 blur-2xl" data-id="element-423"></div>
          </motion.div>
        </div>
      </div>

      <CustomOrderModal open={customOpen} onClose={() => setCustomOpen(false)} data-id="element-424" />
    </section>;
}